import { useCallback, useEffect } from 'react'

import _ from 'lodash'
import { toast } from 'react-toastify'

import { ChainId, NATIVE_TOKEN_ADDRESS, WRAPPED_GAS_TOKENS, getChainNameFromChainId } from '@src/configs/chainInfo'
import {
    MAX_TOKEN_DISPLAY_DECIMALS,
    NATIVE_SETMAX_BUFFER_MULTIPLIERS,
    NATIVE_SETMAX_BUFFER_MULTIPLIER_DEFAULT,
} from '@src/constants/exchange'
import { LiquiditySource, ResultCode } from '@src/enums'
import { useExchangeDataContext } from '@src/pages/Exchange/context/ExchangeDataContext'
import { useExchangeState } from '@src/pages/Exchange/context/ExchangeStateContext'
import { IExchangeData } from '@src/types/exchange'
import { PrecomputeGasResults, TransactionType } from '@src/types/gasPrices'
import { isFeatureEnabled } from '@src/utils'
import { Balance } from '@src/utils/balance'
import { extractMachFee } from '@src/utils/exchangeHelpers'
import { isUnWrapOnly, isWrapOnly } from '@src/utils/exchangeUtils'
import { createContextLogger } from '@src/utils/logger'
import { formatNumberStringWithCommas, returnSanitizedSellAmount } from '@src/utils/sanitizeSellAmount'
import { useTokensStore } from '@src/zustand_stores'

import useExchangeBalances from './useExchangeBalances'

const logger = createContextLogger('useExchangeAmountLogic')

export interface IExchangeAmountLogicParams {
    isComputing: boolean
    isGasPrecomputingDone: boolean
    precomputedGasPrices: PrecomputeGasResults | undefined
}

/**
 * Manages sellAmount, buyAmount, and quote logic.
 */
export function useExchangeAmountLogic({
    isComputing,
    isGasPrecomputingDone,
    precomputedGasPrices,
}: IExchangeAmountLogicParams) {
    const {
        // Everything from your local exchange state (Zustand or context).
        sellAmountString,
        isInsufficientBalance,
        isBuyOutputLoading,
        isQuoteInvalid,
        isManuallyRefreshing,
        liquiditySource,
        isQuoteEnabled,
        setIsQuoteEnabled,
        setSellAmountString,
        setIsInsufficientBalance,
        setIsBuyOutputLoading,
        setIsQuoteInvalid,
        setIsManuallyRefreshing,
        setLiquiditySource,
        setIsOrderRejected,
        setMachFee,
    } = useExchangeState()

    const {
        sellData,
        buyData,
        refetchQuote,
        isQuoteLoading,
        invalidateQuoteAndRefetch,
        forceCancelQuote,
        mainWalletAddress,
        updateSellData,
        updateBuyData,
        quoteResponse,
        destinationAddress,
    } = useExchangeDataContext()

    const { sellBalance, nativeBalance, wrappedNativeBalance } = useExchangeBalances()

    const getGasTokenProps = useTokensStore(state => state.getGasTokenProps)

    const { setHasInsufficientGas, setGasAmountNeededString } = useExchangeState()

    const formatTotalGasNeeded = (sellData: IExchangeData, totalGas: bigint) => {
        try {
            return `${new Balance(getGasTokenProps(sellData.chainId)).setValue(totalGas).formatWithCommas(MAX_TOKEN_DISPLAY_DECIMALS)} ${getGasTokenProps(sellData.chainId).symbol}`
        } catch {
            return ''
        }
    }

    useEffect(() => {
        const formatted = new Balance(sellData.token)
            .setValue(sellData.value)
            .formatWithCommas(MAX_TOKEN_DISPLAY_DECIMALS)
        const sneed = sellAmountString === formatted
        logger.info(
            `[checkSellAmount] Verifying sellAmountString to update isQuoteEnabled:\nformatted=${formatted}\nsellAmountString=${sellAmountString}`
        )
        logger.info(`[checkSellAmount] Setting isQuoteEnabled: ${isQuoteEnabled}->${sneed}`)
        setIsQuoteEnabled(sneed)
        setTimeout(() => {
            logger.info('[checkSellAmount] Now refetching...')
            refetchQuote()
        }, 500)
    }, [sellAmountString, sellData.value, sellData.token.decimals])

    /**
     * Debounced function that sets the final BigInt sell value.
     */
    const debouncedUpdateSellValue = _.debounce((bigintValue: bigint) => {
        logger.info('[debouncedUpdateSellValue] Updating sell value to ', bigintValue.toString())
        updateSellData({ value: bigintValue })
    }, 500)

    // Update loading state based on query status
    useEffect(() => {
        setIsBuyOutputLoading(isQuoteEnabled && isQuoteLoading)
    }, [isQuoteLoading])

    // Set loading to false if quote is not enabled
    useEffect(() => {
        if (!isQuoteEnabled) {
            setIsBuyOutputLoading(false)
        }
    }, [isQuoteEnabled])

    /**
     * Handle raw input changes, then use debounce to update the actual store state.
     */
    const handleOnChangeSellValue = useCallback(
        (input: string) => {
            try {
                const bigintValue = new Balance(sellData.token).parseString(input).bigintValue
                logger.info('[handleOnChangeSellValue] input =', input, 'bigintValue=', bigintValue)

                if (!input || bigintValue === BigInt(0)) {
                    logger.info(`[handleOnChangeSellValue] bigintValue =${bigintValue} Updating sellData...`)
                    updateSellData({ value: BigInt(0) })
                    setIsBuyOutputLoading(false)
                    setIsInsufficientBalance(false)
                    return
                }

                const isSellBalLessThanCurrentVal = sellBalance ? sellBalance.bigintValue < bigintValue : false
                logger.info('[handleOnChangeSellValue] isSellBalLessThanCurrentVal =', isSellBalLessThanCurrentVal)
                setIsInsufficientBalance(isSellBalLessThanCurrentVal)

                debouncedUpdateSellValue(bigintValue)
            } catch (e) {
                logger.error('[handleOnChangeSellValue] error:', e)
            }
        },
        [sellData.token]
    )

    /**
     * UI event handler for text input changes.
     */
    const onChangeSellValue = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            logger.info('[onChangeSellValue] called.')

            const sanitized = returnSanitizedSellAmount(e.target.value, sellData.token.decimals)
            logger.info('[onChangeSellValue] sanitized = ', sanitized)
            if (sanitized == undefined) {
                return // Avoid clearing input when the user presses a non-numeric button by mistake.
            }

            if (sanitized !== sellAmountString) {
                setSellAmountString(formatNumberStringWithCommas(sanitized) || '')

                if (isWrapOnly(sellData, buyData) || isUnWrapOnly(sellData, buyData)) {
                    logger.info(
                        'Setting sell value = buy value. Reson: ',
                        `isWrapOnly(sellData, buyData)=${isWrapOnly(sellData, buyData)}`,
                        `isUnWrapOnly(sellData, buyData)=${isUnWrapOnly(sellData, buyData)}`
                    )
                    const bigintValue = new Balance(sellData.token).parseString(sanitized || '').bigintValue
                    updateSellData({ value: bigintValue })
                    updateBuyData({ value: bigintValue })
                    setIsBuyOutputLoading(false)

                    if (sellBalance && sellBalance.bigintValue < bigintValue) {
                        setIsInsufficientBalance(true)
                    } else {
                        setIsInsufficientBalance(false)
                    }
                    return
                }

                debouncedUpdateSellValue.cancel()

                if (!sanitized) {
                    handleOnChangeSellValue('')
                } else {
                    handleOnChangeSellValue(sanitized)
                }
            }
        },
        [
            handleOnChangeSellValue,
            debouncedUpdateSellValue,
            sellAmountString,
            sellData,
            buyData,
            sellBalance ? sellBalance.bigintValue : BigInt(0),
        ]
    )

    /**
     * Trigger a manual refresh of the quote.
     */
    const handleManualRefresh = useCallback(async () => {
        if (isManuallyRefreshing || !sellData.value || Number(sellData.value) === 0 || sellAmountString === '0') return
        setIsManuallyRefreshing(true)
        try {
            await refetchQuote()
        } finally {
            setIsManuallyRefreshing(false)
        }
    }, [isManuallyRefreshing, refetchQuote, sellData.value, sellAmountString])

    /**
     * Set max amount logic
     */
    const setMax = useCallback(() => {
        logger.info('[setMax] function  called.')
        if (sellBalance === undefined || sellBalance.bigintValue === BigInt(0)) {
            setSellAmountString('')
            updateSellData({ value: BigInt(0) })
            updateBuyData({ value: BigInt(0) })
            setIsInsufficientBalance(false)
            setIsBuyOutputLoading(false)
            return
        }

        setIsBuyOutputLoading(true)
        setLiquiditySource(LiquiditySource.unavailable)

        try {
            let maxAmount = sellBalance.bigintValue

            // Handle native input swaps.
            if (sellData.token.address === NATIVE_TOKEN_ADDRESS[sellData.chainId]) {
                logger.info('[setMax] called with native input, deducing gas fees')
                if (isComputing || !isGasPrecomputingDone || precomputedGasPrices === undefined) {
                    logger.error(
                        '[setMax] invoked at wrong time: need to deduce gas fees, but gas fees are not available. Reason:',
                        `isComputing=`,
                        isComputing,
                        `isGasPrecomputingDone=`,
                        isGasPrecomputingDone,
                        `precomputedGasPrices=`,
                        precomputedGasPrices
                    )
                    toast.error('Unexpected setMax invocation')
                    return
                } else {
                    const wrap_fees = precomputedGasPrices[sellData.chainId].results[TransactionType.WRAP]
                    const approve_fees = precomputedGasPrices[sellData.chainId].results[TransactionType.APPROVE]
                    const place_order_fees = precomputedGasPrices[sellData.chainId].results[TransactionType.PLACE_ORDER]
                    if (wrap_fees && approve_fees && place_order_fees) {
                        let bufferValueNeeded = BigInt(0)
                        if (sellData.chainId === ChainId.Solana) {
                            if (
                                wrap_fees.solanaResults &&
                                approve_fees.solanaResults &&
                                place_order_fees.solanaResults
                            ) {
                                bufferValueNeeded += wrap_fees.solanaResults.feeInLamports
                                bufferValueNeeded += approve_fees.solanaResults.feeInLamports
                                bufferValueNeeded += place_order_fees.solanaResults.feeInLamports
                            } else {
                                logger.error('Wrong data encountered while estimating SOL gas fees')
                                toast.error('Unexpected setMax Error')
                                return
                            }
                        } else if (sellData.chainId === ChainId.Tron) {
                            if (
                                wrap_fees.tronResults &&
                                wrap_fees.feeMetrics.TronParams &&
                                approve_fees.tronResults &&
                                approve_fees.feeMetrics.TronParams &&
                                place_order_fees.tronResults &&
                                place_order_fees.feeMetrics.TronParams
                            ) {
                                bufferValueNeeded +=
                                    wrap_fees.tronResults.totalEnergyUsed * wrap_fees.feeMetrics.TronParams.energyPrice
                                bufferValueNeeded +=
                                    approve_fees.tronResults.totalEnergyUsed *
                                    approve_fees.feeMetrics.TronParams.energyPrice
                                bufferValueNeeded +=
                                    place_order_fees.tronResults.totalEnergyUsed *
                                    place_order_fees.feeMetrics.TronParams.energyPrice

                                bufferValueNeeded +=
                                    wrap_fees.tronResults.totalBandWidthUsed *
                                    wrap_fees.feeMetrics.TronParams.bandwidthPrice
                                bufferValueNeeded +=
                                    approve_fees.tronResults.totalBandWidthUsed *
                                    approve_fees.feeMetrics.TronParams.bandwidthPrice
                                bufferValueNeeded +=
                                    place_order_fees.tronResults.totalBandWidthUsed *
                                    place_order_fees.feeMetrics.TronParams.bandwidthPrice
                            } else {
                                logger.error('Wrong data encountered while estimating Tron gas fees')
                                toast.error('Unexpected setMax Error')
                                return
                            }
                        } else {
                            // Other EVM chains
                            if (
                                wrap_fees.totalGasUnits &&
                                wrap_fees.totalFeePerGasUnit &&
                                approve_fees.totalGasUnits &&
                                approve_fees.totalFeePerGasUnit &&
                                place_order_fees.totalGasUnits &&
                                place_order_fees.totalFeePerGasUnit
                            ) {
                                bufferValueNeeded += wrap_fees.totalGasUnits * wrap_fees.totalFeePerGasUnit
                                bufferValueNeeded += approve_fees.totalGasUnits * approve_fees.totalFeePerGasUnit
                                bufferValueNeeded +=
                                    place_order_fees.totalGasUnits * place_order_fees.totalFeePerGasUnit
                            } else if (
                                sellData.chainId === ChainId.Celo &&
                                approve_fees.totalGasUnits &&
                                approve_fees.totalFeePerGasUnit &&
                                place_order_fees.totalGasUnits &&
                                place_order_fees.totalFeePerGasUnit
                            ) {
                                bufferValueNeeded += approve_fees.totalGasUnits * approve_fees.totalFeePerGasUnit
                                bufferValueNeeded +=
                                    place_order_fees.totalGasUnits * place_order_fees.totalFeePerGasUnit
                            } else {
                                logger.error('Wrong data encountered while estimating EVM gas fees', {
                                    wrap_fees,
                                    approve_fees,
                                    place_order_fees,
                                })
                                toast.error('Unexpected setMax Error')
                                return
                            }
                        }

                        bufferValueNeeded =
                            bufferValueNeeded *
                            (NATIVE_SETMAX_BUFFER_MULTIPLIERS[sellData.chainId]
                                ? NATIVE_SETMAX_BUFFER_MULTIPLIERS[sellData.chainId]
                                : NATIVE_SETMAX_BUFFER_MULTIPLIER_DEFAULT)

                        logger.info('[setMax] native buffer needed:', bufferValueNeeded)

                        if (bufferValueNeeded > maxAmount) {
                            logger.info('[setMax] Insufficient gas for buffered funds.')
                            setHasInsufficientGas(true)
                            setGasAmountNeededString(formatTotalGasNeeded(sellData, bufferValueNeeded))
                            updateBuyData({ value: BigInt(0) })
                            setSellAmountString('0')
                            updateSellData({ value: BigInt(0) })
                            return
                        } else {
                            setHasInsufficientGas(false)
                            maxAmount = maxAmount - bufferValueNeeded
                            // continue control flow after deducing buffer
                        }
                    } else {
                        logger.error('[setMax] precomputedGasPrices has no data for the current chain id.')
                        toast.error('Unexpected setMax invocation')
                        return
                    }
                }
            }

            updateSellData({ value: maxAmount })

            if (isWrapOnly(sellData, buyData) || isUnWrapOnly(sellData, buyData)) {
                updateBuyData({ value: maxAmount })
            }

            setIsInsufficientBalance(false)

            const formatted = new Balance(sellData.token)
                .setValue(maxAmount)
                .formatWithCommas(MAX_TOKEN_DISPLAY_DECIMALS)
            setSellAmountString(formatted)
        } catch (error) {
            console.error('[setMax] Error:', error)
            toast.error('Failed to set maximum amount')
            updateBuyData({ value: BigInt(0) })
        } finally {
            setIsBuyOutputLoading(false)
        }
    }, [
        sellBalance,
        sellData,
        buyData,
        isComputing,
        isGasPrecomputingDone,
        precomputedGasPrices,
        nativeBalance,
        wrappedNativeBalance,
    ])

    /**
     * Refetch quote when sell amount changes
     */
    useEffect(() => {
        if (!isWrapOnly(sellData, buyData) && !isUnWrapOnly(sellData, buyData)) {
            if (sellData.value && sellData.value !== BigInt(0) && sellAmountString !== '0') {
                const qp = {
                    walletAddress: mainWalletAddress ?? '',
                    srcChain: getChainNameFromChainId(sellData.chainId),
                    dstChain: getChainNameFromChainId(buyData.chainId),
                    srcAssetAddress: sellData.token.address,
                    dstAssetAddress: buyData.token.address,
                    srcAmount: sellData.value,
                    targetAddress: destinationAddress || '',
                }
                invalidateQuoteAndRefetch(qp)
            }
        }
    }, [sellData.value])

    /**
     * Handle quote response changes
     */
    useEffect(() => {
        if (sellAmountString === '0') {
            // This means that the user deleted the sell amount string before the quote arrived.
            // See FE-578 in linear
            setIsBuyOutputLoading(false)
            updateBuyData({ value: BigInt(0) })
            return
        } else if (!isBuyOutputLoading && !isWrapOnly(sellData, buyData) && !isUnWrapOnly(sellData, buyData)) {
            if (quoteResponse.status === ResultCode.failure) {
                setIsBuyOutputLoading(false)
                updateBuyData({ value: BigInt(quoteResponse.quote?.dst_amount || 0) })
            } else if (quoteResponse.status === ResultCode.pending) {
                setIsBuyOutputLoading(true)
                logger.info('useEffect: setting isBuyOutputLoading to true.')
            } else {
                if (quoteResponse.quote && 'reject_order' in quoteResponse.quote) {
                    setLiquiditySource(quoteResponse.quote.liquidity_source as LiquiditySource)
                    setIsOrderRejected(quoteResponse.quote.reject_order)

                    if (isFeatureEnabled('USE_TESTNETS_ONLY')) {
                        if (quoteResponse.quote.reject_order) {
                            setIsQuoteInvalid(true)
                            updateBuyData({ value: BigInt(quoteResponse.quote.dst_amount) })
                            setIsBuyOutputLoading(false)
                            return
                        }
                    } else if (quoteResponse.quote.reject_order) {
                        setIsQuoteInvalid(true)
                        updateBuyData({ value: BigInt(0) })
                        return
                    }

                    if (quoteResponse.quote.dst_asset_address?.toLowerCase() === buyData.token.address.toLowerCase()) {
                        updateBuyData({ value: BigInt(quoteResponse.quote.dst_amount) })
                        setIsQuoteInvalid(false)
                        setMachFee(extractMachFee(sellData, quoteResponse.quote))
                        setIsBuyOutputLoading(false)
                    } else {
                        if (buyData.token.native === true) {
                            if (
                                WRAPPED_GAS_TOKENS[buyData.chainId] &&
                                quoteResponse.quote.dst_asset_address.toLowerCase() ===
                                    WRAPPED_GAS_TOKENS[buyData.chainId].address.toLowerCase()
                            ) {
                                updateBuyData({ value: BigInt(quoteResponse.quote.dst_amount) })
                                setIsQuoteInvalid(false)
                                setMachFee(extractMachFee(sellData, quoteResponse.quote))
                                setIsBuyOutputLoading(false)
                            } else if (!WRAPPED_GAS_TOKENS[buyData.chainId]) {
                                throw new Error(`no wrapped gas found for chainId ${buyData.chainId}`)
                            } else {
                                logger.warn('[useExchangeAmountLogic] Reloading quote due to address mismatch:', {
                                    quoteAddress: quoteResponse.quote.dst_asset_address,
                                    wrappedAddress: WRAPPED_GAS_TOKENS[buyData.chainId]?.address,
                                    buyToken: buyData.token,
                                })
                                refetchQuote()
                            }
                        } else {
                            logger.warn('[useExchangeAmountLogic] Reloading quote due to address mismatch:', {
                                quoteAddress: quoteResponse.quote.dst_asset_address,
                                buyToken: buyData.token,
                            })
                            refetchQuote()
                        }
                    }
                }
            }
        }
    }, [isBuyOutputLoading, quoteResponse, sellAmountString])

    // Fix for wrap/unwrap FE-451
    useEffect(() => {
        if (isWrapOnly(sellData, buyData) || isUnWrapOnly(sellData, buyData)) {
            logger.info('useEffect: setting buyData.value to sellData.value')
            updateBuyData({ value: sellData.value })
        }
    }, [sellData.token, buyData.token])

    // Check balance effect
    useEffect(() => {
        const sneed = sellBalance !== undefined && sellBalance.bigintValue < sellData.value
        logger.info('[useEffect] setting isInsufficientBalance to:', sneed, `sellData=`, sellData)
        setIsInsufficientBalance(sneed)
    }, [sellData, quoteResponse.quote, sellBalance])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            debouncedUpdateSellValue.cancel()
        }
    }, [])

    return {
        sellAmountString,
        setSellAmountString,
        isInsufficientBalance,
        isBuyOutputLoading,
        isQuoteInvalid,
        onChangeSellValue,
        handleManualRefresh,
        setMax,
        liquiditySource,
    }
}
